import {
    ButtonBase,
    Checkbox,
    DialogContent,
    FormControlLabel,
    FormGroup,
    TextField,
    IconButton,
    Radio,
} from "@mui/material";
import _, { set, toInteger } from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import NumPad from "react-numpad";
import { AppContext } from "../../../../AppContext";
import backIcon from "../../../../images/back-icon.svg";
import { ItemDialog, ItemSmallButton, useStyles } from "./style";
import { PosContext } from "../../../../shared/PosContext";
import { isRoleAdmin } from "../../../../frappeConfig";

function AddItemWidget({
    item,
    onClose,
    open,
    onAddItem,
    onUpdateItem,
    pushItemWorker,
    editedOrderItem,
}) {
    const classes = useStyles();
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [bundleStep, setBundleStep] = useState(0);

    const { userInfo, elevatedPrivileges } = useContext(AppContext);

    const { addonsItems } = useContext(PosContext);

    const addonItems = useMemo(() => {
        if (addonsItems) {
            return addonsItems;
        }
        return [];
    }, [addonsItems]);

    const [qty, setQty] = useState(1);
    const [notes, setNotes] = useState("");
    const [isEditingPrice, setIsEditingPrice] = useState(false);

    const { menuAddons } = useContext(AppContext);

    const [itemRate, setItemRate] = useState(0);
    const [total, setTotal] = useState(0);
    const [addonsTotal, setAddonsTotal] = useState(0);

    const reset = () => {
        setSelectedAddons([]);
        setSelectedOptions({});
        setQty(1);
        setNotes("");
        setItemRate(0);
        setTotal(0);
        setAddonsTotal(0);
        setIsEditingPrice(false);
    };

    const menuBundle = useMemo(() => {
        try {
            if (item && item.menu_item_bundle) {
                const parsed = JSON.parse(item.menu_item_bundle);
                if (
                    parsed &&
                    Array.isArray(parsed.categories) &&
                    parsed.categories.length > 0
                ) {
                    return parsed;
                }
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return { categories: parsed };
                }
            }
        } catch (e) {}
        return null;
    }, [item]);

    useEffect(() => {
        if (editedOrderItem) {
            setItemRate(editedOrderItem.item.standard_rate);
            const tmpQty = editedOrderItem.qty;
            setNotes(editedOrderItem.notes);
            setQty(tmpQty);

            // Split addons into options vs extra addons based on menu bundle / allowed_addons
            const existingAddons = Array.isArray(editedOrderItem.addons)
                ? editedOrderItem.addons
                : [];

            const allowedAddons = item?.allowed_addons || [];

            // Build defaults
            const defaults = {};
            if (menuBundle && menuBundle.categories) {
                menuBundle.categories.forEach((cat) => {
                    if (cat.options && cat.options.length > 0) {
                        defaults[cat.name] = cat.options[0];
                    }
                });
            }

            const optionsMap = { ...defaults };
            if (menuBundle && menuBundle.categories) {
                menuBundle.categories.forEach((cat) => {
                    const found = existingAddons.find((ad) =>
                        cat.options.some(
                            (opt) => opt.item_code === ad.item_code
                        )
                    );
                    if (found) {
                        const opt = cat.options.find(
                            (o) => o.item_code === found.item_code
                        );
                        if (opt) optionsMap[cat.name] = opt;
                    }
                });
            }

            setSelectedOptions(optionsMap);
            // Extra addons: those that are in allowed_addons but not part of options
            const extraAddons = existingAddons
                .map((i) => i.item_code)
                .filter(
                    (code) =>
                        allowedAddons.includes(code) &&
                        !(menuBundle && menuBundle.categories
                            ? menuBundle.categories.some((cat) =>
                                  cat.options.some(
                                      (opt) => opt.item_code === code
                                  )
                              )
                            : false)
                );
            setSelectedAddons(extraAddons);
        } else {
            if (item) {
                const { standard_rate, notes } = item;
                setItemRate(standard_rate);
                setNotes(notes);
                setQty(1);
                setTotal(standard_rate * 1);

                // Default select first option of each category
                if (menuBundle && menuBundle.categories) {
                    const defaults = {};
                    menuBundle.categories.forEach((cat) => {
                        if (cat.options && cat.options.length > 0) {
                            defaults[cat.name] = cat.options[0];
                        }
                    });
                    setSelectedOptions(defaults);
                } else {
                    setSelectedOptions({});
                }
            } else {
                setItemRate(0);
                setQty(1);
                setNotes("");
                setSelectedOptions({});
            }
        }
        setBundleStep(0);
    }, [item, open, editedOrderItem, menuBundle]);

    useEffect(() => {
        const calculateAddonsTotal = () => {
            let computedAddonsTotal = 0;
            const base = itemRate * qty;

            // Extra addons (from allowed_addons) use system addon rates
            if (menuAddons && selectedAddons && selectedAddons.length > 0) {
                selectedAddons.forEach((oneItem) => {
                    const foundItem = _.find(menuAddons, {
                        item_code: oneItem,
                    });
                    if (foundItem) {
                        computedAddonsTotal +=
                            (foundItem.standard_rate || 0) * qty;
                    }
                });
            }

            // Options (from menu_item_bundle) use added_price
            if (selectedOptions && Object.keys(selectedOptions).length > 0) {
                Object.values(selectedOptions).forEach((opt) => {
                    const added = parseFloat(opt.added_price) || 0;
                    computedAddonsTotal += added * qty;
                });
            }

            setTotal(base + computedAddonsTotal);
            setAddonsTotal(computedAddonsTotal);
        };

        const timer = setTimeout(calculateAddonsTotal, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [qty, selectedAddons, selectedOptions, itemRate, menuAddons]);

    const handleNotesChange = (event) => {
        setNotes(event.target.value);
    };

    const addQty = (addedQty) => {
        if (qty + addedQty > 0) setQty(qty + addedQty);
    };

    const handleClose = () => {
        reset();
        if (onClose) {
            onClose();
        }
    };

    const handleClick = () => {
        if (editedOrderItem) {
            const updatedEntry = {
                ...editedOrderItem,
                qty,
                addons: (() => {
                    const optionsAddons = Object.values(
                        selectedOptions || {}
                    ).map((opt) => ({
                        item_code: opt.item_code,
                        standard_rate: Number(opt.added_price) || 0,
                    }));
                    return [...selectedAddons, ...optionsAddons];
                })(),
                notes,
            };

            if (updatedEntry.item) {
                updatedEntry.item.qty = qty;
                updatedEntry.item.addons = (() => {
                    const optionsAddons = Object.values(
                        selectedOptions || {}
                    ).map((opt) => ({
                        item_code: opt.item_code,
                        standard_rate: Number(opt.added_price) || 0,
                    }));
                    return [...selectedAddons, ...optionsAddons];
                })();
                updatedEntry.item.notes = notes;
                // Update the rate if admin edited it
                if (isRoleAdmin(userInfo.role_profile_name)) {
                    updatedEntry.item.standard_rate = itemRate;
                }
            }
            onUpdateItem(updatedEntry, false);
        } else {
            // Create item with potentially edited rate for admin users
            const itemToAdd = {
                ...item,
                qty: qty,
                notes: notes,
                // Use edited rate if admin, otherwise use original rate
                standard_rate: isRoleAdmin(userInfo.role_profile_name)
                    ? itemRate
                    : item.standard_rate,
            };
            const optionsAddons = Object.values(selectedOptions || {}).map(
                (opt) => ({
                    item_code: opt.item_code,
                    standard_rate: Number(opt.added_price) || 0,
                })
            );
            const combinedAddons = [...selectedAddons, ...optionsAddons];
            onAddItem(itemToAdd, qty, combinedAddons, notes, true);
        }
        handleClose();
        // setTimeout(reset, 1000);
    };

    const handleCheckboxChange = (event) => {
        const { checked, value } = event.target;

        setSelectedAddons((prev) => {
            if (checked) {
                return _.xor(prev, [value]);
            } else {
                return _.without(selectedAddons, value);
            }
        });
    };

    const handlePriceChange = (event) => {
        const value = event.target.value;
        // Allow empty string during editing
        if (value === "") {
            setItemRate("");
        } else {
            const newPrice = parseFloat(value);
            if (!isNaN(newPrice) && newPrice >= 0) {
                setItemRate(newPrice);
            }
        }
    };

    const handlePriceFocus = (event) => {
        // Select all text when field is focused
        event.target.select();
    };

    const handlePriceEditStart = () => {
        setIsEditingPrice(true);
    };

    const handlePriceEditSave = () => {
        // If field is empty or invalid, revert to original value
        if (itemRate === "" || itemRate < 0 || isNaN(itemRate)) {
            const originalRate = editedOrderItem
                ? editedOrderItem.item.standard_rate
                : item.standard_rate;
            setItemRate(originalRate);
        }
        setIsEditingPrice(false);
    };

    const handlePriceEditCancel = () => {
        // Revert to original value
        const originalRate = editedOrderItem
            ? editedOrderItem.item.standard_rate
            : item.standard_rate;
        setItemRate(originalRate);
        setIsEditingPrice(false);
    };

    const handlePriceKeyPress = (event) => {
        if (event.key === "Enter") {
            handlePriceEditSave();
        } else if (event.key === "Escape") {
            handlePriceEditCancel();
        }
    };

    return item ? (
        <>
            <ItemDialog
                className={classes.DialogWrap}
                onClose={() => {
                    handleClose();
                }}
                open={open}
            >
                <DialogContent className={classes.ContentWrapper}>
                    <div>
                        <button className="BackBtn">
                            <img
                                src={backIcon}
                                onClick={() => {
                                    handleClose();
                                }}
                                alt="Back"
                            />
                        </button>
                    </div>

                    <div className={classes.Header}>
                        <div className={classes.ItemDesc}>
                            <img
                                className={classes.ItemDescImage}
                                src={item.cover}
                                alt={item.item_code}
                            />
                            <div>
                                <div className={classes.ItemDescName}>
                                    {item.item_code}
                                </div>
                                {isRoleAdmin(userInfo.role_profile_name) ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        {isEditingPrice ? (
                                            <TextField
                                                type="number"
                                                value={itemRate}
                                                onChange={handlePriceChange}
                                                onFocus={handlePriceFocus}
                                                onKeyDown={handlePriceKeyPress}
                                                onBlur={handlePriceEditSave}
                                                variant="outlined"
                                                size="small"
                                                autoFocus
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.01,
                                                    style: { fontSize: "14px" },
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root":
                                                        {
                                                            height: "32px",
                                                            "& fieldset": {
                                                                borderColor:
                                                                    "#ddd",
                                                            },
                                                            "&:hover fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#999",
                                                                },
                                                            "&.Mui-focused fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#1976d2",
                                                                },
                                                        },
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <div
                                                    className={
                                                        classes.ItemDescPrice
                                                    }
                                                >
                                                    {itemRate || 0}
                                                </div>
                                                {elevatedPrivileges ||
                                                isRoleAdmin(
                                                    userInfo.role_profile_name
                                                ) ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={
                                                            handlePriceEditStart
                                                        }
                                                        sx={{
                                                            padding: "4px",
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "rgba(0, 0, 0, 0.04)",
                                                            },
                                                        }}
                                                    >
                                                        ✏️
                                                    </IconButton>
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className={classes.ItemDescPrice}>
                                        {item.standard_rate}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.ItemQty}>
                            <ItemSmallButton
                                onClick={() => addQty(-1)}
                                className={classes.ItemQtyMinus}
                            >
                                -
                            </ItemSmallButton>
                            <NumPad.Number
                                onChange={(value) => {
                                    setQty(value > 0 ? toInteger(value) : 1);
                                }}
                                isDecimal={false}
                                position={"startBottomRight"}
                                min={1}
                                value={0}
                            >
                                <div className={classes.ItemQtyText}>{qty}</div>
                            </NumPad.Number>
                            <ItemSmallButton
                                onClick={() => addQty(1)}
                                className={classes.ItemQtyPlus}
                            >
                                +
                            </ItemSmallButton>
                        </div>
                    </div>

                    <div className={classes.ScrollableContent}>
                        {menuBundle && menuBundle.categories && (
                            <div className={classes.Addons}>
                                <div>Options</div>
                                <div className={classes.OptionsList}>
                                    <div className={classes.WizardHeader}>
                                        <span>
                                            Step{" "}
                                            {Math.min(
                                                bundleStep + 1,
                                                menuBundle?.categories
                                                    ?.length || 1
                                            )}{" "}
                                            of{" "}
                                            {menuBundle?.categories?.length ||
                                                1}
                                        </span>
                                        <span>
                                            {
                                                menuBundle.categories[
                                                    bundleStep
                                                ]?.name
                                            }
                                        </span>
                                    </div>
                                    {menuBundle.categories[bundleStep] && (
                                        <div
                                            key={
                                                menuBundle.categories[
                                                    bundleStep
                                                ].name
                                            }
                                            style={{ padding: "0.25rem" }}
                                        >
                                            <div
                                                className={classes.OptionsGrid}
                                            >
                                                {menuBundle.categories[
                                                    bundleStep
                                                ].options &&
                                                    menuBundle.categories[
                                                        bundleStep
                                                    ].options.map((opt) => (
                                                        <div
                                                            className={
                                                                classes.OptionItem
                                                            }
                                                            key={`${menuBundle.categories[bundleStep].name}_${opt.item_code}`}
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Radio
                                                                        checked={
                                                                            selectedOptions?.[
                                                                                menuBundle
                                                                                    .categories[
                                                                                    bundleStep
                                                                                ]
                                                                                    .name
                                                                            ]
                                                                                ?.item_code ===
                                                                            opt.item_code
                                                                        }
                                                                        onChange={() => {
                                                                            const catName =
                                                                                menuBundle
                                                                                    .categories[
                                                                                    bundleStep
                                                                                ]
                                                                                    .name;
                                                                            setSelectedOptions(
                                                                                (
                                                                                    prev
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    [catName]:
                                                                                        opt,
                                                                                })
                                                                            );
                                                                        }}
                                                                        value={
                                                                            opt.item_code
                                                                        }
                                                                    />
                                                                }
                                                                label={`${opt.label}`}
                                                            />
                                                            <span
                                                                className={
                                                                    classes.OptionPrice
                                                                }
                                                            >
                                                                {opt.added_price ||
                                                                    0}
                                                            </span>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className={classes.WizardActions}>
                                        <ButtonBase
                                            className={classes.StepButton}
                                            onClick={() =>
                                                setBundleStep((s) =>
                                                    Math.max(0, s - 1)
                                                )
                                            }
                                            disabled={bundleStep === 0}
                                        >
                                            Back
                                        </ButtonBase>
                                        <ButtonBase
                                            className={classes.StepButton}
                                            onClick={() =>
                                                setBundleStep((s) =>
                                                    Math.min(
                                                        (menuBundle?.categories
                                                            ?.length || 1) - 1,
                                                        s + 1
                                                    )
                                                )
                                            }
                                            disabled={
                                                bundleStep >=
                                                (menuBundle?.categories
                                                    ?.length || 1) -
                                                    1
                                            }
                                        >
                                            Next
                                        </ButtonBase>
                                    </div>
                                </div>
                            </div>
                        )}

                        {menuBundle && menuBundle.categories && (
                            <div className={classes.Notes}>
                                <div>Notes</div>
                                <textarea
                                    value={notes}
                                    onChange={handleNotesChange}
                                    placeholder="Add notes here"
                                    className={classes.NotesArea}
                                ></textarea>
                            </div>
                        )}
                        <div className={classes.Addons}>
                            <div>Extra Add-ons</div>
                            <FormGroup
                                className={classes.AddonsList}
                                style={
                                    menuBundle && menuBundle.categories
                                        ? { maxHeight: "180px" }
                                        : {}
                                }
                            >
                                {item.allowed_addons
                                    ? item.allowed_addons.map((addonsItem) => {
                                          const addon =
                                              addonItems.find(
                                                  (i) =>
                                                      i.item_code === addonsItem
                                              ) || {};
                                          return (
                                              <div
                                                  className={classes.AddonsRaw}
                                                  key={addonsItem}
                                              >
                                                  <FormControlLabel
                                                      control={
                                                          <Checkbox
                                                              onChange={
                                                                  handleCheckboxChange
                                                              }
                                                              checked={
                                                                  selectedAddons.find(
                                                                      (i) =>
                                                                          i ===
                                                                          addonsItem
                                                                  ) != null
                                                              }
                                                              value={addonsItem}
                                                          />
                                                      }
                                                      label={`${addonsItem} `}
                                                  />
                                                  <span>
                                                      {addon.standard_rate}
                                                  </span>
                                              </div>
                                          );
                                      })
                                    : ""}
                            </FormGroup>
                        </div>

                        {(!menuBundle || !menuBundle.categories) && (
                            <div className={classes.Notes}>
                                <div>Notes</div>
                                <textarea
                                    value={notes}
                                    onChange={handleNotesChange}
                                    placeholder="Add notes here"
                                    className={classes.NotesArea}
                                ></textarea>
                            </div>
                        )}
                    </div>

                    <div className={classes.Footer}>
                        <div>
                            <div className={classes.TotalText}>Total</div>
                            <div>
                                <div className={classes.TotalPrice}>
                                    {total}
                                </div>
                                <div className={classes.AddonsPrice}>
                                    (Add-ons {addonsTotal})
                                </div>
                            </div>
                        </div>
                        <ButtonBase
                            onClick={handleClick}
                            className={classes.DialogAction}
                        >
                            {pushItemWorker.isLoading ? "Saving ..." : "Save"}
                        </ButtonBase>
                    </div>
                </DialogContent>
            </ItemDialog>
        </>
    ) : (
        ""
    );
}

export default AddItemWidget;
